/** Validierung, die Client und Netlify Functions teilen. */

/**
 * Rollen, die jede App mindestens kennt. Welche eine App **tatsaechlich**
 * anbietet, steht in `apps.roles` — der MPS Manager unterscheidet zum
 * Beispiel sechs. Diese Liste ist nur der Rueckfall fuer Apps, die nichts
 * eigenes hinterlegt haben.
 *
 * Warum nicht fest im Code: sonst muesste der Hub bei jeder neuen App
 * angefasst werden, und wer hier eine Rolle vergisst, ueberschreibt beim
 * naechsten Klick stillschweigend die Einstellung einer anderen App.
 */
export const ROLES = ['user', 'admin'] as const;
export type Role = string;

/** Ist der Wert eine Rolle, die diese App anbietet? */
export function isRoleForApp(value: unknown, appRoles: readonly string[]): boolean {
  return typeof value === 'string' && appRoles.includes(value);
}

export const MAX_TITLE_LENGTH = 140;
export const MAX_BODY_LENGTH = 8000;
export const MAX_NAME_LENGTH = 120;

export interface AnnouncementInput {
  title: string;
  body_md: string;
  valid_until: string | null;
  pinned: boolean;
}

export type ValidationResult<T> = { ok: true; value: T } | { ok: false; error: string };

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[a-z]{2,}$/i;
const UUID_PATTERN = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

export function isEmail(value: unknown): value is string {
  return typeof value === 'string' && value.length <= 254 && EMAIL_PATTERN.test(value.trim());
}

export function isUuid(value: unknown): value is string {
  return typeof value === 'string' && UUID_PATTERN.test(value);
}

/** Rueckfall-Pruefung ohne App-Bezug. Bevorzugt `isRoleForApp` verwenden. */
export function isRole(value: unknown): value is Role {
  return typeof value === 'string' && value.length > 0 && value.length <= 40;
}

export function validateAnnouncement(input: unknown): ValidationResult<AnnouncementInput> {
  if (typeof input !== 'object' || input === null) return { ok: false, error: 'Kein Objekt.' };
  const raw = input as Record<string, unknown>;

  const title = typeof raw.title === 'string' ? raw.title.trim() : '';
  if (title.length === 0) return { ok: false, error: 'Titel fehlt.' };
  if (title.length > MAX_TITLE_LENGTH) {
    return { ok: false, error: `Titel ist laenger als ${MAX_TITLE_LENGTH} Zeichen.` };
  }

  const body = typeof raw.body_md === 'string' ? raw.body_md.trim() : '';
  if (body.length === 0) return { ok: false, error: 'Text fehlt.' };
  if (body.length > MAX_BODY_LENGTH) {
    return { ok: false, error: `Text ist laenger als ${MAX_BODY_LENGTH} Zeichen.` };
  }

  let validUntil: string | null = null;
  if (raw.valid_until !== undefined && raw.valid_until !== null && raw.valid_until !== '') {
    if (typeof raw.valid_until !== 'string') return { ok: false, error: 'Ungueltiges Datum.' };
    const parsed = new Date(raw.valid_until);
    if (Number.isNaN(parsed.getTime())) return { ok: false, error: 'Ungueltiges Datum.' };
    validUntil = parsed.toISOString();
  }

  return {
    ok: true,
    value: { title, body_md: body, valid_until: validUntil, pinned: raw.pinned === true },
  };
}

export function validateInvite(
  input: unknown,
): ValidationResult<{ email: string; display_name: string | null }> {
  if (typeof input !== 'object' || input === null) return { ok: false, error: 'Kein Objekt.' };
  const raw = input as Record<string, unknown>;

  if (!isEmail(raw.email)) return { ok: false, error: 'Ungueltige E-Mail-Adresse.' };

  let displayName: string | null = null;
  if (typeof raw.display_name === 'string' && raw.display_name.trim().length > 0) {
    displayName = raw.display_name.trim().slice(0, MAX_NAME_LENGTH);
  }

  return { ok: true, value: { email: raw.email.trim().toLowerCase(), display_name: displayName } };
}

/**
 * Konto mit Zugangsdaten anlegen — im Unterschied zur Einladung, bei der die
 * Person ihr Passwort selbst setzt.
 *
 * Das Passwort darf mitgeschickt werden, muss aber nicht: ohne Angabe erzeugt
 * die Function eines. Beides hat seinen Platz — ein selbst gewaehltes laesst
 * sich am Telefon durchgeben, ein erzeugtes ist besser.
 *
 * Mindestlaenge 12. Kuerzer laesst Supabase zwar zu, aber ein Passwort, das
 * ein Admin vergibt und per Chat weiterreicht, lebt laenger als gedacht.
 */
export const MIN_PASSWORT_LAENGE = 12;

export function validateCreateUser(input: unknown): ValidationResult<{
  email: string;
  display_name: string | null;
  zoho_user_id: string | null;
  passwort: string | null;
}> {
  if (typeof input !== 'object' || input === null) return { ok: false, error: 'Kein Objekt.' };
  const raw = input as Record<string, unknown>;

  if (!isEmail(raw.email)) return { ok: false, error: 'Ungueltige E-Mail-Adresse.' };

  let displayName: string | null = null;
  if (typeof raw.display_name === 'string' && raw.display_name.trim().length > 0) {
    displayName = raw.display_name.trim().slice(0, MAX_NAME_LENGTH);
  }

  // Die erecno aus Zoho People. Ziffernfolge, aber als Text gefuehrt — sie ist
  // ein Schluessel, mit dem nicht gerechnet wird.
  let zohoUserId: string | null = null;
  if (typeof raw.zoho_user_id === 'string' && raw.zoho_user_id.trim().length > 0) {
    const kandidat = raw.zoho_user_id.trim();
    if (!/^[A-Za-z0-9_-]{1,64}$/.test(kandidat)) {
      return { ok: false, error: 'Ungueltige Zoho-Kennung.' };
    }
    zohoUserId = kandidat;
  }

  // Kein Trimmen: fuehrender oder folgender Leerraum gehoert zum Passwort,
  // und ein stilles Abschneiden wuerde beim Anmelden scheitern lassen.
  let passwort: string | null = null;
  if (raw.passwort !== undefined && raw.passwort !== null && raw.passwort !== '') {
    if (typeof raw.passwort !== 'string') return { ok: false, error: 'Passwort muss Text sein.' };
    if (raw.passwort.length < MIN_PASSWORT_LAENGE) {
      return { ok: false, error: `Das Passwort braucht mindestens ${MIN_PASSWORT_LAENGE} Zeichen.` };
    }
    if (raw.passwort.length > 200) return { ok: false, error: 'Das Passwort ist zu lang.' };
    passwort = raw.passwort;
  }

  return {
    ok: true,
    value: {
      email: raw.email.trim().toLowerCase(),
      display_name: displayName,
      zoho_user_id: zohoUserId,
      passwort,
    },
  };
}

/** Sortierung des Schwarzen Bretts: gepinnt zuerst, dann neueste. */
export function compareAnnouncements(
  a: { pinned: boolean; created_at: string },
  b: { pinned: boolean; created_at: string },
): number {
  if (a.pinned !== b.pinned) return a.pinned ? -1 : 1;
  return b.created_at.localeCompare(a.created_at);
}

/** Laeuft die Ankuendigung noch? (Client-Filter; RLS filtert zusaetzlich serverseitig.) */
export function isVisibleAnnouncement(
  announcement: { valid_until: string | null },
  now: Date = new Date(),
): boolean {
  if (!announcement.valid_until) return true;
  return new Date(announcement.valid_until).getTime() > now.getTime();
}
